import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import {
  Video, Film, Download, Trash2, Loader2, Sparkles, Play, Image as ImageIcon,
  Wand2, Clock, HardDrive, DollarSign, RefreshCw, X, Check, Volume2, VolumeX,
} from 'lucide-react';
import { api, compact, timeAgo } from '../../lib/api';
import { themeOf, ARENA, rarityOf } from '../../lib/classTheme';
import { ArenaTitle, ArenaTabs, ArenaEmpty, GoldFrame, Laurel } from '../../components/ArenaFrame';
import { Sheet, SkeletonList } from '../../components/ui';

export default function AdminVideo() {
  const qc = useQueryClient();
  const [tab, setTab] = useState('create');
  const [preview, setPreview] = useState(null);

  /* ---- selection ---- */
  const [classKey, setClassKey] = useState('warrior');
  const [heroId, setHeroId] = useState(null);
  const [petId, setPetId] = useState(null);
  const [carId, setCarId] = useState(null);
  const [heroRarity, setHeroRarity] = useState('legendary');
  const [petRarity, setPetRarity] = useState('epic');
  const [sceneKind, setSceneKind] = useState('hero_pet_geared');

  /* ---- scene ---- */
  const [scene, setScene] = useState(null);
  const [sceneBusy, setSceneBusy] = useState(false);

  /* ---- video ---- */
  const [prompt, setPrompt] = useState('');
  const [title, setTitle] = useState('');
  const [duration, setDuration] = useState(8);
  const [resolution, setResolution] = useState('720p');
  const [audio, setAudio] = useState(true);
  const [videoBusy, setVideoBusy] = useState(false);
  const [progress, setProgress] = useState(null);
  const pollRef = useRef();

  const { data: meta, isLoading } = useQuery({
    queryKey: ['videoMeta'], queryFn: () => api.get('/video/meta').then(r => r.data),
  });
  const { data: library } = useQuery({
    queryKey: ['videoLibrary'], queryFn: () => api.get('/video').then(r => r.data),
    refetchInterval: tab === 'library' ? 15000 : false,
  });

  /* auto-select first assets of the class */
  useEffect(() => {
    if (!meta) return;
    const h = meta.assets.heroes.find(x => x.class_key === classKey);
    const p = meta.assets.pets.find(x => x.class_key === classKey);
    const c = meta.assets.cars.find(x => x.class_key === classKey);
    setHeroId(h?.id || null);
    setPetId(p?.id || null);
    setCarId(c?.id || null);
    setScene(null);
  }, [classKey, meta]);

  const heroes = meta?.assets.heroes.filter(x => x.class_key === classKey) || [];
  const pets   = meta?.assets.pets.filter(x => x.class_key === classKey) || [];
  const cars   = meta?.assets.cars.filter(x => x.class_key === classKey) || [];

  const hero = heroes.find(x => x.id === heroId);
  const pet  = pets.find(x => x.id === petId);
  const car  = cars.find(x => x.id === carId);
  const t = themeOf(classKey);

  /* ---------------- SCENE ---------------- */
  async function buildScene(force = false) {
    if (!heroId) return toast.error('Select a hero first');
    setSceneBusy(true);
    try {
      const { data } = await api.post('/video/scene', {
        kind: sceneKind,
        heroTemplateId: heroId,
        petTemplateId: sceneKind.includes('pet') ? petId : null,
        carTemplateId: sceneKind.includes('car') || sceneKind === 'full_squad' ? carId : null,
        heroRarity, petRarity, force,
      });
      setScene(data);
      toast.success(data.cached ? 'Loaded from cache' : `Scene created (${data.refsUsed} refs)`);
    } catch (e) { toast.error(e.message); } finally { setSceneBusy(false); }
  }

  /* ---------------- VIDEO ---------------- */
  async function generate() {
    if (!prompt || prompt.trim().length < 10)
      return toast.error('Write a descriptive prompt (min 10 chars)');
    if (!scene?.image) return toast.error('Build the composite scene first');

    setVideoBusy(true);
    setProgress({ status: 'SUBMITTING' });

    try {
      const req = api.post('/video/generate', {
        title: title || undefined,
        prompt,
        sceneId: scene.sceneId,
        duration, resolution, generateAudio: audio,
        classKey, heroTemplateId: heroId,
        petTemplateId: petId, carTemplateId: carId,
        itemRarity: heroRarity,
      });

      // poll progress
      const started = Date.now();
      pollRef.current = setInterval(async () => {
        try {
          const lib = await api.get('/video').then(r => r.data);
          const gen = lib.videos.find(v => v.status === 'generating');
          if (gen) {
            const p = await api.get(`/video/${gen.id}/progress`).then(r => r.data);
            setProgress({ ...p.progress, status: p.progress?.status || 'GENERATING',
              elapsedMs: Date.now() - started });
          }
        } catch {}
      }, 4000);

      const { data } = await req;
      clearInterval(pollRef.current);
      setProgress(null);
      toast.success(`🎬 Video ready! ${data.sizeMb}MB in ${Math.round(data.ms / 1000)}s`, { duration: 6000 });
      qc.invalidateQueries(['videoLibrary', 'videoMeta']);
      setTab('library');
    } catch (e) {
      clearInterval(pollRef.current);
      setProgress(null);
      toast.error(e.message);
    } finally { setVideoBusy(false); }
  }

  useEffect(() => () => clearInterval(pollRef.current), []);

  const del = useMutation({
    mutationFn: (id) => api.delete(`/video/${id}`),
    onSuccess: () => { toast.success('Deleted'); qc.invalidateQueries(['videoLibrary']); },
  });

  if (isLoading || !meta) return <SkeletonList n={4} h="h-32" />;

  const cost = (duration * (meta.config.costPerSecond || 0.06)).toFixed(2);

  return (
    <div className="space-y-4">
      {/* ═══ HEADER ═══ */}
      <div className="card-arena p-4 flex items-center gap-3">
        <div className="w-11 h-11 rounded-2xl grid place-items-center"
          style={{ background: 'rgba(232,200,116,.18)', border: `1px solid ${ARENA.gold}` }}>
          <Film size={20} style={{ color: ARENA.goldL }} />
        </div>
        <div className="flex-1">
          <h2 className="font-display text-lg font-extrabold" style={{ color: ARENA.goldL }}>
            Video Studio
          </h2>
          <p className="text-[11px] font-semibold" style={{ color: 'rgba(232,220,200,.65)' }}>
            {meta.config.model} · {meta.config.remaining}/{meta.config.dailyLimit} left today
          </p>
        </div>
        <div className="text-right">
          <p className="font-display font-black text-lg" style={{ color: ARENA.goldL }}>
            {library?.stats?.ready || 0}
          </p>
          <p className="text-[9px] font-bold" style={{ color: 'rgba(232,220,200,.5)' }}>VIDEOS</p>
        </div>
      </div>

      <ArenaTabs value={tab} onChange={setTab} tabs={[
        { key: 'create',  label: 'Create', icon: Wand2 },
        { key: 'library', label: 'Library', icon: Film, badge: library?.stats?.total || 0 },
      ]} />

      {/* ═══════════ CREATE ═══════════ */}
      {tab === 'create' && (
        <>
          {/* STEP 1 — assets */}
          <div className="card p-4">
            <ArenaTitle title="1 · Select assets" latin="Elige" />

            {/* class */}
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-1.5 mb-3">
              {Object.values(meta.classes).map(c => {
                const ct = themeOf(c.key);
                const active = classKey === c.key;
                return (
                  <button key={c.key} onClick={() => setClassKey(c.key)}
                    className="p-2 rounded-xl border-2 text-center transition"
                    style={active
                      ? { borderColor: ct.hex, background: `${ct.hex}12` }
                      : { borderColor: 'var(--stone-200)', background: 'var(--marble)' }}>
                    <div className="text-lg">{c.icon}</div>
                    <p className="text-[9px] font-display font-black capitalize mt-0.5">{c.key}</p>
                  </button>
                );
              })}
            </div>

            {/* scene kind */}
            <label className="label">Scene type</label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 mb-3">
              {meta.sceneKinds.map(k => (
                <button key={k.key} onClick={() => setSceneKind(k.key)}
                  className="p-2 rounded-lg text-[10px] font-display font-bold transition"
                  style={sceneKind === k.key
                    ? { background: ARENA.goldFrame, color: '#fff' }
                    : { background: 'var(--stone-100)', color: 'var(--stone-600)' }}>
                  {k.label}
                </button>
              ))}
            </div>

            {/* pickers */}
            <div className="space-y-3">
              <AssetRow label="Hero" items={heroes} value={heroId} onChange={setHeroId} aspect="9/16" />
              {sceneKind.includes('pet') && (
                <AssetRow label="Companion" items={pets} value={petId} onChange={setPetId} aspect="9/16" />
              )}
              {(sceneKind.includes('car') || sceneKind === 'full_squad') && (
                <AssetRow label="Chariot" items={cars} value={carId} onChange={setCarId} aspect="9/16" />
              )}
            </div>

            {/* rarities */}
            <div className="grid grid-cols-2 gap-3 mt-3">
              <div>
                <label className="label">Hero gear rarity</label>
                <RarityPicker value={heroRarity} onChange={setHeroRarity} rarities={meta.rarities} />
              </div>
              {sceneKind === 'hero_pet_geared' && (
                <div>
                  <label className="label">Pet gear rarity</label>
                  <RarityPicker value={petRarity} onChange={setPetRarity} rarities={meta.rarities} />
                </div>
              )}
            </div>
          </div>

          {/* STEP 2 — scene */}
          <div className="card p-4">
            <ArenaTitle title="2 · Build composite scene" latin="Compone"
              right={scene && (
                <button onClick={() => buildScene(true)} disabled={sceneBusy} className="btn-ghost btn-sm">
                  <RefreshCw size={12} /> Regenerate
                </button>
              )} />

            {!scene ? (
              <button onClick={() => buildScene(false)} disabled={sceneBusy || !heroId}
                className="btn-primary w-full py-3">
                {sceneBusy
                  ? <><Loader2 size={16} className="animate-spin" /> Compositing… (~30s)</>
                  : <><ImageIcon size={16} /> Build scene (hero + gear{sceneKind.includes('pet') ? ' + pet' : ''})</>}
              </button>
            ) : (
              <div className="space-y-3">
                {/* source thumbs */}
                <div>
                  <p className="label">Source references</p>
                  <div className="flex gap-2 overflow-x-auto no-scrollbar">
                    {hero && <Thumb src={hero.thumb || hero.image_url} label="Hero" />}
                    {sceneKind.includes('pet') && pet && <Thumb src={pet.thumb || pet.image_url} label="Pet" />}
                    {(sceneKind.includes('car') || sceneKind === 'full_squad') && car &&
                      <Thumb src={car.thumb || car.image_url} label="Car" />}
                    <div className="shrink-0 grid place-items-center px-2 text-lg"
                      style={{ color: ARENA.goldD }}>→</div>
                    <Thumb src={scene.thumb || scene.image} label="Composite" gold big />
                  </div>
                </div>

                {/* big preview */}
                <GoldFrame className="overflow-hidden">
                  <img src={scene.image} alt="" className="w-full object-cover"
                    style={{ maxHeight: 380 }} />
                </GoldFrame>

                <p className="text-[11px] font-semibold text-center" style={{ color: 'var(--stone-400)' }}>
                  {scene.cached ? '♻️ Loaded from cache' : `✨ ${scene.refsUsed} references combined`}
                </p>
              </div>
            )}
          </div>

          {/* STEP 3 — video */}
          <div className="card p-4">
            <ArenaTitle title="3 · Describe the video" latin="Narra" />

            {/* presets */}
            <div className="flex gap-1.5 overflow-x-auto no-scrollbar mb-2">
              {meta.presets.map(p => (
                <button key={p.key}
                  onClick={() => { setPrompt(p.prompt); setDuration(p.duration); setTitle(p.label.replace(/^\S+\s/, '')); }}
                  className="px-3 py-1.5 rounded-lg text-[11px] font-bold whitespace-nowrap transition"
                  style={{ background: 'var(--stone-100)', color: 'var(--stone-600)' }}>
                  {p.label}
                </button>
              ))}
            </div>

            <input className="input mb-2" value={title} maxLength={80}
              placeholder="Video title (optional)"
              onChange={e => setTitle(e.target.value)} />

            <textarea className="input min-h-[110px]" value={prompt} maxLength={1200}
              placeholder="Describe the motion, camera and mood…&#10;e.g. The warrior raises his sword as the drake roars behind him. Slow motion, dramatic camera push-in, embers flying."
              onChange={e => setPrompt(e.target.value)} />
            <p className="text-[10px] text-right mt-0.5" style={{ color: 'var(--stone-400)' }}>
              {prompt.length}/1200
            </p>

            {/* params */}
            <div className="mt-3 space-y-3">
              <div>
                <div className="flex justify-between mb-1.5">
                  <label className="label !mb-0">Duration</label>
                  <span className="font-display font-black text-sm" style={{ color: ARENA.goldD }}>
                    {duration}s
                  </span>
                </div>
                <input type="range" min="4" max={meta.config.maxDuration} value={duration}
                  onChange={e => setDuration(Number(e.target.value))}
                  className="w-full" style={{ accentColor: ARENA.gold }} />
                <div className="flex justify-between text-[9px] font-bold mt-0.5"
                  style={{ color: 'var(--stone-400)' }}>
                  <span>4s</span><span>{meta.config.maxDuration}s</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">Resolution</label>
                  <div className="flex gap-1.5">
                    {meta.config.resolutions.map(r => (
                      <button key={r} onClick={() => setResolution(r)}
                        className="flex-1 py-2 rounded-lg text-xs font-display font-black transition"
                        style={resolution === r
                          ? { background: ARENA.goldFrame, color: '#fff' }
                          : { background: 'var(--stone-100)', color: 'var(--stone-500)' }}>
                        {r}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="label">Audio</label>
                  <button onClick={() => setAudio(v => !v)}
                    className="w-full py-2 rounded-lg text-xs font-display font-black transition
                      flex items-center justify-center gap-1.5"
                    style={audio
                      ? { background: ARENA.goldFrame, color: '#fff' }
                      : { background: 'var(--stone-100)', color: 'var(--stone-500)' }}>
                    {audio ? <Volume2 size={13} /> : <VolumeX size={13} />}
                    {audio ? 'ON' : 'OFF'}
                  </button>
                </div>
              </div>
            </div>

            {/* cost */}
            <div className="mt-3 p-3 rounded-xl flex items-center justify-between"
              style={{ background: 'rgba(201,151,58,.10)', border: `1px solid ${ARENA.gold}33` }}>
              <div className="flex items-center gap-2">
                <DollarSign size={15} style={{ color: ARENA.goldD }} />
                <span className="text-sm font-display font-bold">Estimated cost</span>
              </div>
              <span className="font-display font-black" style={{ color: ARENA.goldD }}>${cost}</span>
            </div>

            {/* progress */}
            <AnimatePresence>
              {progress && (
                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mt-3 p-3 rounded-xl overflow-hidden"
                  style={{ background: 'var(--stone-900)', color: ARENA.goldL }}>
                  <div className="flex items-center gap-2">
                    <Loader2 size={15} className="animate-spin" />
                    <span className="font-display font-bold text-sm">{progress.status}</span>
                    {progress.queuePosition != null && (
                      <span className="chip-ink">queue #{progress.queuePosition}</span>
                    )}
                    <span className="ml-auto text-xs font-mono">
                      {Math.round((progress.elapsedMs || 0) / 1000)}s
                    </span>
                  </div>
                  {progress.logs?.length > 0 && (
                    <div className="mt-2 text-[10px] font-mono space-y-0.5 opacity-70">
                      {progress.logs.slice(-3).map((l, i) => <p key={i}>{l}</p>)}
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>

            <button onClick={generate}
              disabled={videoBusy || !scene?.image || prompt.length < 10 || meta.config.remaining <= 0}
              className="btn-primary w-full py-3.5 mt-3 font-display font-black">
              {videoBusy
                ? <><Loader2 size={17} className="animate-spin" /> Generating… (2–5 min)</>
                : meta.config.remaining <= 0
                  ? 'Daily limit reached'
                  : <><Film size={17} /> Generate video (${cost})</>}
            </button>
          </div>
        </>
      )}

      {/* ═══════════ LIBRARY ═══════════ */}
      {tab === 'library' && (
        <>
          {library?.stats && (
            <div className="grid grid-cols-4 gap-2">
              <Stat icon={Film} v={library.stats.ready} l="Ready" />
              <Stat icon={X} v={library.stats.failed} l="Failed" />
              <Stat icon={HardDrive} v={`${library.stats.sizeMb}MB`} l="Storage" />
              <Stat icon={DollarSign} v={`$${Number(library.stats.cost).toFixed(2)}`} l="Cost" />
            </div>
          )}

          {!library?.videos?.length ? (
            <ArenaEmpty emoji="🎬" title="No videos yet"
              sub="Create your first promotional video in the Create tab."
              action={<button onClick={() => setTab('create')} className="btn-primary btn-sm">
                <Wand2 size={13} /> Create video</button>} />
          ) : (
            <div className="grid sm:grid-cols-2 gap-3">
              {library.videos.map(v => (
                <VideoCard key={v.id} video={v}
                  onPlay={() => setPreview(v)}
                  onDelete={() => confirm('Delete this video?') && del.mutate(v.id)} />
              ))}
            </div>
          )}
        </>
      )}

      {/* ═══ PREVIEW ═══ */}
      <Sheet open={!!preview} onClose={() => setPreview(null)}
        title={preview?.title || preview?.code || 'Video'} wide>
        {preview && (
          <div className="space-y-3">
            <GoldFrame dark className="overflow-hidden">
              <video src={preview.video_url} controls autoPlay loop
                className="w-full" style={{ maxHeight: 500 }} />
            </GoldFrame>

            <div className="grid grid-cols-4 gap-2 text-center">
              <Meta l="Duration" v={`${preview.duration}s`} />
              <Meta l="Quality" v={preview.resolution} />
              <Meta l="Size" v={`${preview.sizeMb}MB`} />
              <Meta l="Cost" v={`$${Number(preview.cost_usd).toFixed(2)}`} />
            </div>

            <div className="card p-3">
              <p className="label">Prompt</p>
              <p className="text-xs" style={{ color: 'var(--stone-600)' }}>{preview.prompt}</p>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <a href={preview.video_url} download className="btn-ghost">
                <Download size={15} /> Open
              </a>
              <a href={`/api/video/${preview.id}/download`} className="btn-primary">
                <Download size={15} /> Download MP4
              </a>
            </div>
          </div>
        )}
      </Sheet>
    </div>
  );
}

/* ============================================================
   SUB-COMPONENTS
   ============================================================ */
function AssetRow({ label, items, value, onChange, aspect }) {
  if (!items.length) {
    return (
      <div>
        <label className="label">{label}</label>
        <p className="text-xs p-3 rounded-xl text-center"
          style={{ background: 'var(--stone-100)', color: 'var(--stone-400)' }}>
          None generated for this class yet
        </p>
      </div>
    );
  }
  return (
    <div>
      <label className="label">{label} ({items.length})</label>
      <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
        {items.map(x => (
          <button key={x.id} onClick={() => onChange(x.id)}
            className="shrink-0 w-[72px] rounded-xl overflow-hidden transition"
            style={{
              padding: 2,
              background: value === x.id ? ARENA.goldFrame : 'var(--stone-200)',
              transform: value === x.id ? 'scale(1.04)' : 'none',
            }}>
            <div className="rounded-lg overflow-hidden bg-stone-200" style={{ aspectRatio: aspect }}>
              <img src={x.thumb_url || x.image_url} alt="" className="w-full h-full object-cover object-top" />
            </div>
            <p className="text-[7px] font-display font-black truncate px-1 py-0.5">
              {x.name?.split(' ')[0]}
            </p>
          </button>
        ))}
      </div>
    </div>
  );
}

function RarityPicker({ value, onChange, rarities }) {
  return (
    <div className="flex gap-1">
      {rarities.map(r => (
        <button key={r.code} onClick={() => onChange(r.code)}
          title={r.label}
          className="flex-1 h-8 rounded-lg text-[9px] font-display font-black transition"
          style={value === r.code
            ? { background: r.color, color: '#fff', boxShadow: `0 2px 10px -2px ${r.color}` }
            : { background: `${r.color}18`, color: r.color }}>
          {r.label.slice(0, 3).toUpperCase()}
        </button>
      ))}
    </div>
  );
}

function Thumb({ src, label, gold, big }) {
  return (
    <div className="shrink-0 text-center">
      <div className={big ? 'w-[86px]' : 'w-[58px]'}
        style={{ padding: 2, background: gold ? ARENA.goldFrame : 'var(--stone-200)', borderRadius: 10 }}>
        <div className="rounded-lg overflow-hidden bg-stone-200" style={{ aspectRatio: '9/16' }}>
          <img src={src} alt="" className="w-full h-full object-cover object-top" />
        </div>
      </div>
      <p className="text-[8px] font-display font-black mt-0.5"
        style={{ color: gold ? ARENA.goldD : 'var(--stone-400)' }}>{label}</p>
    </div>
  );
}

function VideoCard({ video, onPlay, onDelete }) {
  const ok = video.status === 'ready';
  return (
    <div className="card overflow-hidden">
      <button onClick={ok ? onPlay : undefined}
        className="relative block w-full aspect-video"
        style={{ background: 'var(--stone-900)' }}>
        {video.scene_thumb || video.image_url ? (
          <img src={video.scene_thumb || video.image_url} alt=""
            className="w-full h-full object-cover opacity-60" />
        ) : null}
        <div className="absolute inset-0 grid place-items-center">
          {ok ? (
            <div className="w-12 h-12 rounded-full grid place-items-center"
              style={{ background: 'rgba(232,200,116,.9)' }}>
              <Play size={20} className="text-stone-900 ml-0.5" fill="currentColor" />
            </div>
          ) : video.status === 'generating' ? (
            <Loader2 size={24} className="animate-spin" style={{ color: ARENA.goldL }} />
          ) : (
            <X size={24} style={{ color: ARENA.blood }} />
          )}
        </div>
        <span className="absolute bottom-2 right-2 px-1.5 py-0.5 rounded text-[9px] font-black text-white"
          style={{ background: 'rgba(0,0,0,.65)' }}>
          {video.duration}s · {video.resolution}
        </span>
      </button>

      <div className="p-3">
        <p className="font-display font-bold text-sm truncate">
          {video.title || video.code}
        </p>
        <p className="text-[11px] font-semibold truncate" style={{ color: 'var(--stone-400)' }}>
          {video.hero_name}{video.pet_name && ` + ${video.pet_name}`}
          {video.car_name && ` + ${video.car_name}`}
        </p>
        <p className="text-[10px] mt-0.5" style={{ color: 'var(--stone-400)' }}>
          {timeAgo(video.created_at)} · {video.sizeMb}MB · ${Number(video.cost_usd).toFixed(2)}
          {video.downloads > 0 && ` · ${video.downloads}↓`}
        </p>

        {video.error && (
          <p className="text-[10px] mt-1 font-bold" style={{ color: ARENA.blood }}>
            {video.error.slice(0, 80)}
          </p>
        )}

        <div className="flex gap-1.5 mt-2">
          {ok && (
            <a href={`/api/video/${video.id}/download`} className="btn-primary btn-sm flex-1">
              <Download size={12} /> Download
            </a>
          )}
          <button onClick={onDelete} className="btn btn-sm"
            style={{ background: 'rgba(139,38,53,.10)', color: ARENA.blood }}>
            <Trash2 size={12} />
          </button>
        </div>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, v, l }) {
  return (
    <div className="card p-2.5 text-center">
      <Icon size={14} className="mx-auto mb-1" style={{ color: 'var(--stone-400)' }} />
      <p className="font-display font-black text-base">{v}</p>
      <p className="text-[9px] font-display font-bold uppercase" style={{ color: 'var(--stone-400)' }}>{l}</p>
    </div>
  );
}

function Meta({ l, v }) {
  return (
    <div className="rounded-xl p-2" style={{ background: 'var(--sand-100)' }}>
      <p className="text-[9px] font-display font-black uppercase" style={{ color: 'var(--stone-400)' }}>{l}</p>
      <p className="font-display font-bold text-sm mt-0.5">{v}</p>
    </div>
  );
}